import type { RouteErrorKind } from '@shared/utils/route-error-kind';

import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { Link, useLocation, useRouteError } from 'react-router';

import { Button } from '@shared/components/ui/button';
import { FatalError } from '@shared/components/fatal-error';
import { getRouteErrorKind } from '@shared/utils/route-error-kind';

const CONTENT: Record<RouteErrorKind, { title: string; message: string }> = {
  'not-found': {
    title: 'Page introuvable',
    message: 'Cette page n’existe pas ou a été déplacée.',
  },
  outdated: {
    title: 'Nouvelle version disponible',
    message: 'L’application a été mise à jour : rechargez la page pour continuer.',
  },
  unexpected: {
    title: 'Une erreur est survenue',
    message: 'Cette page n’a pas pu s’afficher. Si le problème revient après avoir rechargé, signalez-le.',
  },
};

type Props = {
  /** True above the layout, which crashed too: there is no menu left around the fallback. */
  fullPage?: boolean;
};

/** Route error boundary. React Router catches these errors before Sentry does, so they are reported from here. */
export const RouteError = ({ fullPage = false }: Props) => {
  const error = useRouteError();
  const { pathname } = useLocation();

  const kind = getRouteErrorKind(error);
  const { title, message } = CONTENT[kind];

  useEffect(() => {
    if (kind === 'unexpected') {
      Sentry.captureException(error);
    }
  }, [error, kind]);

  return (
    <FatalError fullPage={fullPage} message={message} title={title}>
      <div className="flex flex-wrap justify-center gap-2">
        {kind !== 'not-found' && <Button onClick={() => window.location.reload()}>Recharger la page</Button>}
        {pathname !== '/' && (
          <Button asChild variant="outline">
            <Link to="/">Retour à l’accueil</Link>
          </Button>
        )}
      </div>
      {kind === 'unexpected' && (
        <details className="max-w-xl text-xs text-muted-foreground">
          <summary className="cursor-pointer">Détails techniques</summary>
          <p className="mt-2 break-words">{error instanceof Error ? error.message : String(error)}</p>
        </details>
      )}
    </FatalError>
  );
};
